import React, { useEffect } from 'react';
import { colors } from '../styles/variables';
import CourseHeader from '../components/course/CourseHeader';
import CourseAbout from '../components/course/CourseAbout';
import CourseCurriculum from '../components/course/CourseCurriculum';
import CourseProjects from '../components/course/CourseProjects';
import CourseInstructors from '../components/course/CourseInstructors';
import CourseCta from '../components/course/CourseCta';
import CourseReviewSection from '../components/course/CourseReviewSection';
import Seo from '../components/common/Seo';
import { projectsByCourse } from '../data/projects';

const courseJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Course',
  name: 'Java 기초 프로그래밍',
  description: 'Java 기초부터 객체지향, 실전 프로젝트까지 체계적으로 배우는 입문자 맞춤 1:1 멘토링 과정.',
  provider: {
    '@type': 'EducationalOrganization',
    name: 'ElectroWave Club',
    sameAs: 'https://electrowave.kr',
  },
  inLanguage: 'ko',
  educationalLevel: 'beginner',
  url: 'https://electrowave.kr/java',
};

const JavaPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  // 코스 헤더 데이터
  const headerData = {
    tag: '객체 지향 프로그래밍',
    title: 'Java 기초 과정',
    description: 'Java 프로그래밍의 기초부터 객체지향 개념, 자료구조까지 체계적으로 학습합니다. 코딩 경험이 적은 분들도 쉽게 이해할 수 있는 커리큘럼으로 백엔드 개발자의 첫 걸음을 시작하세요.',
    duration: '총 750시간 / 30주 과정',
    level: '입문자 대상',
    bgColor: colors.javaColor,
    bgImage: '/assets/images/course-thumb-java.png'
  };
  
  // 코스 소개 데이터
  const aboutData = {
    subtitle: '다양한 산업 분야에서 활용되는 Java 언어의 기초부터 심화 개념까지 체계적으로 배울 수 있는 과정입니다.',
    forWhom: [
      '프로그래밍을 처음 시작하는 비전공자',
      '백엔드 개발자로 취업을 준비하는 분',
      '객체지향 프로그래밍의 개념을 제대로 이해하고 싶은 분',
      '알고리즘과 자료구조를 실습을 통해 배우고 싶은 분',
      '실무에 적용 가능한 프로젝트 경험을 쌓고 싶은 분'
    ],
    skills: [
      'Java 기초 문법과 객체지향 프로그래밍 이해',
      '자료구조와 알고리즘 구현 능력',
      '예외 처리와 파일 입출력 활용 기술',
      'Git을 활용한 버전 관리와 협업 능력',
      '테스트 주도 개발(TDD)의 기본 역량',
      'Java 프로젝트 설계 및 구현 능력'
    ],
    imageSrc: '/assets/images/course-thumb-java.png'
  };
  
  // 커리큘럼 데이터
  const curriculumData = {
    subtitle: '입문자도 따라올 수 있는 단계적 학습과 실습 중심의 커리큘럼으로 Java 개발 역량을 체계적으로 키웁니다.',
    modules: [
      {
        title: '1. Java 프로그래밍 입문',
        description: 'Java 개발 환경을 설정하고 기본 문법을 학습합니다.',
        lessons: [
          'Java 소개 및 개발 환경 설정',
          '변수, 자료형, 연산자',
          '제어문: 조건문, 반복문',
          '배열과 문자열 다루기',
          '기본 입출력',
          '메서드의 개념과 활용'
        ]
      },
      {
        title: '2. 객체지향 프로그래밍 (OOP)',
        description: '객체지향 프로그래밍의 핵심 개념을 배우고 Java에서 구현하는 방법을 익힙니다.',
        lessons: [
          '객체와 클래스의 개념',
          '생성자와 소멸자',
          '접근 제어자와 정보 은닉',
          '상속과 다형성',
          '추상 클래스와 인터페이스',
          '캡슐화와 객체지향 설계 원칙'
        ]
      },
      {
        title: '3. 자료구조 및 알고리즘',
        description: '코딩 테스트와 실무에 활용되는 자료구조와 알고리즘을 Java로 구현합니다.',
        lessons: [
          '자료구조 기초 (배열, 리스트, 스택, 큐)',
          'Java Collection Framework',
          'LinkedList와 ArrayList 구현 및 활용',
          '해시맵과 해시셋',
          '트리와 그래프의 기본 개념',
          '정렬 및 검색 알고리즘',
          '재귀와 동적 프로그래밍 기초'
        ]
      },
      {
        title: '4. 고급 Java 프로그래밍',
        description: '실무에서 활용되는 고급 Java 기능과 기술을 학습합니다.',
        lessons: [
          '예외 처리와 사용자 정의 예외',
          '파일 입출력 (File I/O)',
          '스레딩과 동시성',
          '네트워킹 기초 (소켓 프로그래밍)',
          'JDBC를 이용한 데이터베이스 연동',
          '람다 표현식과 스트림 API'
        ]
      },
      {
        title: '5. Java 프레임워크 및 라이브러리',
        description: '실무에서 많이 사용되는 Java 기반 프레임워크와 라이브러리의 기초를 배웁니다.',
        lessons: [
          'Spring Framework 기초',
          '의존성 주입(DI)과 제어의 역전(IoC)',
          'Spring MVC 아키텍처',
          'Hibernate를 이용한 ORM',
          'RESTful API 개발',
          'Maven과 Gradle을 이용한 프로젝트 관리'
        ]
      }
    ]
  };
  
  // 프로젝트 데이터 (상세 콘텐츠는 src/data/projects.js)
  const projectsData = {
    subtitle: '수업에서 배운 내용을 바로 적용할 수 있는 실전형 Java 프로젝트를 수행합니다.',
    projects: projectsByCourse.java,
  };
  
  // 강사진 데이터
  const instructorsData = {
    subtitle: 'Java 개발과 교육 분야에서 풍부한 경험을 갖춘 전문가들이 여러분의 성장을 돕습니다.',
    instructors: [
      {
        name: 'Rechard Park',
        title: '시니어 백엔드 개발자',
        bio: '10년 이상의 Java 개발 경력을 보유한 백엔드 개발자로, 대규모 시스템 설계 및 구현에 전문성을 갖추고 있습니다. 현업 경험을 바탕으로 실무에서 바로 적용 가능한 Java 기술을 가르치는 데 중점을 둡니다.',
        image: '/assets/images/instructor-park-jiwon.png'
      },
    ]
  };
  
  // CTA 데이터
  const ctaData = {
    title: 'Java 개발자의 첫 걸음을 시작하세요',
    subtitle: 'ElectroWave Club의 Java 기초 과정을 통해 객체지향 프로그래밍의 핵심을 배우고 실무 역량을 갖춘 백엔드 개발자로 성장하세요.',
    bgColor: colors.javaColor,
    endColor: '#2C3E50'  // 어두운 파란색 계열
  };
  
  return (
    <>
      <Seo
        title="Java 기초 프로그래밍"
        description="Java 기초부터 객체지향, 실전 프로젝트까지 체계적으로 배우는 입문자 맞춤 1:1 멘토링 강의."
        path="/java"
        ogImage="/assets/images/course-thumb-java.png"
        ogType="article"
        jsonLd={courseJsonLd}
      />
      <CourseHeader {...headerData} />
      <CourseAbout {...aboutData} />
      <CourseCurriculum {...curriculumData} />
      <CourseProjects {...projectsData} courseSlug="java" />
      <CourseInstructors {...instructorsData} />
      <CourseReviewSection courseTitle="Java 기초" />
      <CourseCta {...ctaData} />
    </>
  );
};

export default JavaPage;
